import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { teamMembersAPI } from '../lib/api';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    Users,
    Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Team() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            const response = await teamMembersAPI.getAll();
            setMembers(response.data);
        } catch (error) {
            toast.error('Failed to load team members');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setSelectedMember(null);
        setFormData({ name: '', email: '' });
        setDialogOpen(true);
    };

    const openEditDialog = (member) => {
        setSelectedMember(member);
        setFormData({ name: member.name, email: member.email });
        setDialogOpen(true);
    };

    const openDeleteDialog = (member) => {
        setSelectedMember(member);
        setDeleteDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (selectedMember) {
                await teamMembersAPI.update(selectedMember.id, formData);
                toast.success('Team member updated');
            } else {
                await teamMembersAPI.create(formData);
                toast.success('Team member added');
            }
            setDialogOpen(false);
            fetchMembers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save team member');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await teamMembersAPI.delete(selectedMember.id);
            toast.success('Team member removed');
            setDeleteDialogOpen(false);
            fetchMembers();
        } catch (error) {
            toast.error('Failed to delete team member');
        }
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getAvatarColor = (name) => {
        const colors = [
            'bg-orange-600',
            'bg-emerald-600',
            'bg-blue-600',
            'bg-purple-600',
            'bg-pink-600',
            'bg-amber-600',
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="team-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Team
                        </h1>
                        <p className="text-lg text-zinc-500">
                            Manage your team members
                        </p>
                    </div>
                    <Button 
                        onClick={openCreateDialog} 
                        className="swiss-btn-primary"
                        data-testid="add-member-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Member
                    </Button>
                </div>

                {/* Team Grid */}
                {members.length === 0 ? (
                    <div className="swiss-card animate-fade-in">
                        <div className="empty-state">
                            <Users className="empty-state-icon" />
                            <h3 className="text-xl font-semibold text-zinc-900 mb-2">No team members yet</h3>
                            <p className="text-zinc-500 mb-4">Add your first team member to start tracking their time</p>
                            <Button onClick={openCreateDialog} className="swiss-btn-primary" data-testid="add-first-member-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Member
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {members.map((member, index) => (
                            <div 
                                key={member.id} 
                                className="swiss-card animate-fade-in"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                data-testid={`member-card-${member.id}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-14 h-14 ${getAvatarColor(member.name)} rounded-sm flex items-center justify-center`}>
                                        <span className="text-white font-bold text-lg">{getInitials(member.name)}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => openEditDialog(member)}
                                            className="h-8 w-8 text-zinc-500 hover:text-zinc-900"
                                            data-testid={`edit-member-${member.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => openDeleteDialog(member)}
                                            className="h-8 w-8 text-zinc-500 hover:text-red-600"
                                            data-testid={`delete-member-${member.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {member.name}
                                </h3>
                                <div className="flex items-center gap-2 text-zinc-500">
                                    <Mail className="w-4 h-4" />
                                    <span className="text-sm truncate">{member.email}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {selectedMember ? 'Edit Team Member' : 'Add Team Member'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="member-form">
                        <div className="form-group">
                            <Label htmlFor="name" className="form-label">Full Name</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., John Doe"
                                required
                                className="swiss-input"
                                data-testid="member-name-input"
                            />
                        </div>
                        <div className="form-group">
                            <Label htmlFor="email" className="form-label">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="e.g., john@example.com"
                                required
                                className="swiss-input"
                                data-testid="member-email-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="swiss-btn-primary" disabled={saving} data-testid="member-submit-btn">
                                {saving ? 'Saving...' : selectedMember ? 'Update' : 'Add'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove "{selectedMember?.name}" from your team and delete all their time entries. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-member"
                        >
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
